import Phaser from 'phaser';
import { App } from './App';
import {
  COLORS,
  FIELD_GUIDE_WINDOW,
  FONTS,
  TASKBAR,
  toHexColor,
} from '../config/gameConfig';
import type { BadgePayload, EventBus, Term } from '../systems/EventBus';
import { TEACH_PANELS } from '../content/teach-panels';
import { SECONDARY_TERMS, type SecondaryTermId } from '../content/secondary-terms';
import { formatFictionalTime } from '../systems/Clock';
import type { UnlockRecord } from '../systems/SessionTracker';
import { Scrollable } from '../ui/Scrollable';

const CORE_ORDER: readonly Term[] = [
  'aging-bucket',
  'dunning',
  'cash-application',
  'short-pay',
  'dispute',
  'promise-to-pay',
  'write-off',
  'dso',
];

const SECONDARY_ORDER: readonly SecondaryTermId[] = [
  'net-30',
  'remittance-advice',
  'credit-memo',
  'unapplied-cash',
  'po-matching',
];

interface UnlockSource {
  snapshot(): { termUnlocks: ReadonlyMap<Term, UnlockRecord> };
}

export class FieldGuideApp extends App {
  private listContainer!: Phaser.GameObjects.Container;
  private scrollable!: Scrollable;
  private highlightedTerm: Term | null = null;
  private pulseActive = false;
  private pulseTimer: Phaser.Time.TimerEvent | null = null;
  private rowsByTerm = new Map<Term | SecondaryTermId, Phaser.GameObjects.Rectangle>();

  private readonly onTermUnlocked = (p: { term: Term }) => {
    this.rerender();
    this.triggerPulse(p.term);
  };

  private readonly onFocusTerm = (p: { term: Term }) => {
    if (this.isMinimized) {
      this.bus.emit('window:restore-requested', { windowId: this.options.windowId });
    } else {
      this.bus.emit('window:focus-requested', { windowId: this.options.windowId });
    }
    this.highlightedTerm = p.term;
    this.rerender();
    this.scene.time.delayedCall(FIELD_GUIDE_WINDOW.highlightFlashMs, () => {
      this.highlightedTerm = null;
      this.rerender();
    });
  };

  constructor(
    scene: Phaser.Scene,
    bus: EventBus,
    private readonly tracker: UnlockSource,
  ) {
    super(scene, bus, {
      x: FIELD_GUIDE_WINDOW.x,
      y: FIELD_GUIDE_WINDOW.y,
      width: FIELD_GUIDE_WINDOW.width,
      height: FIELD_GUIDE_WINDOW.height,
      title: 'Field Guide',
      windowId: 'field-guide',
      titleBarHeight: FIELD_GUIDE_WINDOW.titleBarHeight,
    });
    this.renderBody();
    this.bus.on('term:unlocked', this.onTermUnlocked);
    this.bus.on('field-guide:focus-term', this.onFocusTerm);

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());

    this.emitBadge();
  }

  getBadge(): BadgePayload {
    const unlocked = this.tracker.snapshot().termUnlocks.size;
    return {
      label: 'Field Guide',
      count: unlocked,
      pulse: this.pulseActive,
      iconColor: COLORS.markedTermUnderline,
    };
  }

  private emitBadge(): void {
    this.bus.emit('window:badge-changed', { windowId: this.options.windowId, badge: this.getBadge() });
  }

  private triggerPulse(_term: Term): void {
    this.pulseActive = true;
    this.emitBadge();
    if (this.pulseTimer) this.pulseTimer.remove();
    this.pulseTimer = this.scene.time.delayedCall(TASKBAR.pulseHoldAfterEventMs, () => {
      this.pulseActive = false;
      this.pulseTimer = null;
      this.emitBadge();
    });
  }

  protected onRestored(): void {
    this.pulseActive = false;
    if (this.pulseTimer) {
      this.pulseTimer.remove();
      this.pulseTimer = null;
    }
    this.emitBadge();
  }

  protected onResize(): void {
    this.scrollable?.resize(this.bodyWidth, this.bodyHeight);
    this.rerender();
  }

  protected renderBody(): void {
    this.listContainer = this.scene.add.container(0, 0);
    this.bodyContainer.add(this.listContainer);
    this.scrollable = new Scrollable(this.scene, this, this.listContainer, this.bodyContainer);
    this.rerender();
  }

  private rerender(): void {
    if (!this.listContainer) return;
    this.listContainer.removeAll(true);
    this.rowsByTerm.clear();

    const unlocks = this.tracker.snapshot().termUnlocks;
    let y = FIELD_GUIDE_WINDOW.sectionTopGap;

    y = this.renderSectionHeader(y, 'CORE TERMS');

    for (const term of CORE_ORDER) {
      const unlock = unlocks.get(term);
      y = this.renderTermRow(term, y, TEACH_PANELS[term].title, TEACH_PANELS[term].definition, unlock);
    }

    y += FIELD_GUIDE_WINDOW.sectionBottomGap;
    y = this.renderSectionHeader(y, 'OTHER VOCABULARY');

    for (const id of SECONDARY_ORDER) {
      const entry = SECONDARY_TERMS[id];
      y = this.renderSecondaryRow(id, y, entry.title, entry.definition);
    }

    this.scrollable?.setContentHeight(y + 12);
    this.refreshChildDepths();
  }

  private renderSectionHeader(y: number, label: string): number {
    const header = this.scene.add.text(
      FIELD_GUIDE_WINDOW.rowPaddingX,
      y,
      label,
      {
        fontFamily: FONTS.ui,
        fontSize: FIELD_GUIDE_WINDOW.sectionHeaderFontSize,
        color: toHexColor(COLORS.textMuted),
        fontStyle: 'bold',
      },
    );
    this.listContainer.add(header);
    return y + header.height + FIELD_GUIDE_WINDOW.sectionBottomGap;
  }

  private renderDivider(y: number): void {
    const line = this.scene.add
      .rectangle(FIELD_GUIDE_WINDOW.rowPaddingX, y, this.bodyWidth - FIELD_GUIDE_WINDOW.rowPaddingX * 2, 1, COLORS.emailSeparator)
      .setOrigin(0, 0);
    this.listContainer.add(line);
  }

  private renderTermRow(
    term: Term,
    y: number,
    title: string,
    definition: string,
    unlock: UnlockRecord | undefined,
  ): number {
    if (!unlock) return this.renderLockedRow(term, y, title);

    // Unlocked: title + small inline timestamp + wrapped definition.
    const isHighlighted = this.highlightedTerm === term;
    const rowTop = y;

    const titleText = this.scene.add.text(
      FIELD_GUIDE_WINDOW.rowPaddingX,
      y + FIELD_GUIDE_WINDOW.rowPaddingY,
      title,
      {
        fontFamily: FONTS.ui,
        fontSize: FIELD_GUIDE_WINDOW.unlockedTitleFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    this.listContainer.add(titleText);

    const ts = this.scene.add.text(
      this.bodyWidth - FIELD_GUIDE_WINDOW.rowPaddingX,
      y + FIELD_GUIDE_WINDOW.rowPaddingY + 2,
      `unlocked ${formatFictionalTime(unlock.fictionalMinutes)}`,
      {
        fontFamily: FONTS.ui,
        fontSize: FIELD_GUIDE_WINDOW.unlockedTimestampFontSize,
        color: toHexColor(COLORS.textMuted),
      },
    ).setOrigin(1, 0);
    this.listContainer.add(ts);

    const defWrap = this.bodyWidth - FIELD_GUIDE_WINDOW.rowPaddingX * 2;
    const def = this.scene.add.text(
      FIELD_GUIDE_WINDOW.rowPaddingX,
      y + FIELD_GUIDE_WINDOW.rowPaddingY + titleText.height + 6,
      definition,
      {
        fontFamily: FONTS.ui,
        fontSize: FIELD_GUIDE_WINDOW.unlockedDefinitionFontSize,
        color: toHexColor(COLORS.textPrimary),
        wordWrap: { width: defWrap, useAdvancedWrap: true },
        lineSpacing: FIELD_GUIDE_WINDOW.unlockedDefinitionLineSpacing,
      },
    );
    this.listContainer.add(def);

    const rowHeight = FIELD_GUIDE_WINDOW.rowPaddingY + titleText.height + 6 + def.height + FIELD_GUIDE_WINDOW.rowPaddingY;
    const bg = this.scene.add
      .rectangle(
        0,
        rowTop,
        this.bodyWidth,
        rowHeight,
        isHighlighted ? COLORS.markedTermHighlight : COLORS.windowBody,
        isHighlighted ? 0.18 : 0,
      )
      .setOrigin(0, 0);
    this.listContainer.addAt(bg, 0);
    this.rowsByTerm.set(term, bg);

    const nextY = rowTop + rowHeight;
    this.renderDivider(nextY);
    return nextY + FIELD_GUIDE_WINDOW.rowGap;
  }

  private renderLockedRow(term: Term, y: number, title: string): number {
    // Compact one-line locked row: lock icon + dimmed title. The visual
    // style alone says "you'll see this once it shows up today."
    const centerY = y + FIELD_GUIDE_WINDOW.lockedRowHeight / 2;

    const lock = this.scene.add.text(
      FIELD_GUIDE_WINDOW.rowPaddingX,
      centerY,
      '🔒',
      {
        fontFamily: FONTS.ui,
        fontSize: FIELD_GUIDE_WINDOW.lockedFontSize,
      },
    ).setOrigin(0, 0.5);
    this.listContainer.add(lock);

    const lockedTitle = this.scene.add.text(
      FIELD_GUIDE_WINDOW.rowPaddingX + lock.width + 10,
      centerY,
      title,
      {
        fontFamily: FONTS.ui,
        fontSize: FIELD_GUIDE_WINDOW.lockedFontSize,
        color: toHexColor(FIELD_GUIDE_WINDOW.lockedColor),
        fontStyle: 'bold',
      },
    ).setOrigin(0, 0.5);
    this.listContainer.add(lockedTitle);

    const bg = this.scene.add
      .rectangle(0, y, this.bodyWidth, FIELD_GUIDE_WINDOW.lockedRowHeight, COLORS.windowBody, 0)
      .setOrigin(0, 0);
    this.listContainer.addAt(bg, 0);
    this.rowsByTerm.set(term, bg);

    const nextY = y + FIELD_GUIDE_WINDOW.lockedRowHeight;
    this.renderDivider(nextY);
    return nextY + FIELD_GUIDE_WINDOW.rowGap;
  }

  private renderSecondaryRow(id: SecondaryTermId, y: number, title: string, definition: string): number {
    const titleText = this.scene.add.text(
      FIELD_GUIDE_WINDOW.rowPaddingX,
      y + FIELD_GUIDE_WINDOW.rowPaddingY,
      title,
      {
        fontFamily: FONTS.ui,
        fontSize: FIELD_GUIDE_WINDOW.unlockedTitleFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    this.listContainer.add(titleText);

    const def = this.scene.add.text(
      FIELD_GUIDE_WINDOW.rowPaddingX,
      y + FIELD_GUIDE_WINDOW.rowPaddingY + titleText.height + 6,
      definition,
      {
        fontFamily: FONTS.ui,
        fontSize: FIELD_GUIDE_WINDOW.unlockedDefinitionFontSize,
        color: toHexColor(COLORS.textPrimary),
        wordWrap: { width: this.bodyWidth - FIELD_GUIDE_WINDOW.rowPaddingX * 2, useAdvancedWrap: true },
        lineSpacing: FIELD_GUIDE_WINDOW.unlockedDefinitionLineSpacing,
      },
    );
    this.listContainer.add(def);

    const rowHeight = FIELD_GUIDE_WINDOW.rowPaddingY + titleText.height + 6 + def.height + FIELD_GUIDE_WINDOW.rowPaddingY;
    const nextY = y + rowHeight;
    this.renderDivider(nextY);
    return nextY + FIELD_GUIDE_WINDOW.rowGap;
  }

  private teardown(): void {
    this.bus.off('term:unlocked', this.onTermUnlocked);
    this.bus.off('field-guide:focus-term', this.onFocusTerm);
    this.scrollable?.destroy();
    if (this.pulseTimer) {
      this.pulseTimer.remove();
      this.pulseTimer = null;
    }
  }
}
